import type { HousingRtmsBandDefinition, HousingRtmsBandRow, HousingRtmsScopeId, HousingRtmsStatus } from "./adapters/housingRtms";
import type { IndicatorSeries, Unit } from "../types";

export type HousingChartMetric = "count" | "share" | "price";
export type HousingChartSmoothing = "raw" | "ma3";

function previousMonth(month: string) {
  const [year, monthNumber] = month.split("-").map(Number);
  const date = new Date(Date.UTC(year, monthNumber - 2, 1));
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}`;
}

function metricUnit(metric: HousingChartMetric): Unit {
  if (metric === "count") return "count";
  if (metric === "share") return "%";
  return "eok_krw";
}

function metricValue(row: HousingRtmsBandRow, metric: HousingChartMetric) {
  if (metric === "count") return row.transactionCount;
  if (metric === "share") return row.transactionSharePct;
  return row.medianPriceProxyKrw == null ? null : row.medianPriceProxyKrw / 100_000_000;
}

function ma3ByBand(rows: HousingRtmsBandRow[], bandId: string, metric: Exclude<HousingChartMetric, "price">) {
  const byMonth = new Map(rows.filter((row) => row.bandId === bandId).map((row) => [row.month, metricValue(row, metric)]));
  const result = new Map<string, number | null>();
  for (const [month, value] of byMonth) {
    const prev1 = byMonth.get(previousMonth(month));
    const prev2 = byMonth.get(previousMonth(previousMonth(month)));
    result.set(month, typeof value === "number" && typeof prev1 === "number" && typeof prev2 === "number"
      ? (value + prev1 + prev2) / 3
      : null);
  }
  return result;
}

export function rtmsBandSeries(args: {
  rows: HousingRtmsBandRow[];
  bands: HousingRtmsBandDefinition[];
  scope: HousingRtmsScopeId;
  scopeLabel: string;
  status: HousingRtmsStatus;
  metric: HousingChartMetric;
  smoothing?: HousingChartSmoothing;
  idPrefix: string;
  dataset: string;
  generatedAt?: string | null;
  suppressThinPriceBelow?: number;
}): IndicatorSeries[] {
  const smoothing = args.smoothing ?? "raw";
  return args.bands.map((band) => {
    const ma3 = args.metric === "price" || smoothing === "raw" ? null : ma3ByBand(args.rows, band.id, args.metric);
    const observations = args.rows
      .filter((row) => row.bandId === band.id)
      .sort((a, b) => a.month.localeCompare(b.month))
      .map((row) => {
        let value = metricValue(row, args.metric);
        if (args.metric === "price" && args.suppressThinPriceBelow != null && row.transactionCount < args.suppressThinPriceBelow) value = null;
        if (ma3) value = ma3.get(row.month) ?? null;
        return { date: `${row.month}-01`, value: value == null ? null : Number(value.toFixed(6)) };
      });
    const metricName = args.metric === "count" ? "거래건수" : args.metric === "share" ? "거래비중" : "거래가격 proxy";
    const smoothName = smoothing === "ma3" && args.metric !== "price" ? " 3개월 평균" : "";
    return {
      id: `${args.idPrefix}_${band.id}_${args.metric}_${smoothing}`,
      name: `${args.scopeLabel} ${band.label} ${metricName}${smoothName}`,
      shortName: band.label,
      unit: metricUnit(args.metric),
      layer: "real_estate",
      source: {
        provider: "국토교통부",
        dataset: args.dataset,
        url: "https://rt.molit.go.kr/",
        frequency: "monthly",
        quality: args.status === "error" || args.status === "not_collected" ? "unavailable" : "official",
        observationDate: observations.filter((item) => item.value != null).at(-1)?.date,
        collectedAt: args.generatedAt ?? undefined,
        nativeUnit: args.metric === "price" ? "억원 proxy" : args.metric === "count" ? "건" : "%",
        note: [
          "국토교통부 아파트 실거래를 월·지역·가격구간으로 집계한 계열입니다.",
          args.metric === "price" ? "가격은 동일주택 가격지수가 아니라 시군구 중앙가격의 거래건수 가중 proxy이며 composition bias가 있을 수 있습니다." : "",
          smoothing === "ma3" && args.metric !== "price" ? "연속한 3개 월 관측이 모두 있을 때만 3개월 평균을 계산합니다." : "",
          args.suppressThinPriceBelow != null && args.metric === "price" ? `${args.suppressThinPriceBelow}건 미만 월은 가격 proxy를 N/A 처리합니다.` : ""
        ].filter(Boolean).join(" ")
      },
      observations
    } satisfies IndicatorSeries;
  });
}

export function totalTransactionsSeries(args: {
  rows: HousingRtmsBandRow[];
  scope: HousingRtmsScopeId;
  scopeLabel: string;
  status: HousingRtmsStatus;
  idPrefix?: string;
  dataset?: string;
  generatedAt?: string | null;
}): IndicatorSeries {
  const totals = new Map<string, number>();
  for (const row of args.rows) totals.set(row.month, (totals.get(row.month) ?? 0) + row.transactionCount);
  const observations = [...totals.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([month, value]) => ({ date: `${month}-01`, value }));
  return {
    id: `${args.idPrefix ?? "housing_rtms_total"}_${args.scope}`,
    name: `${args.scopeLabel} 아파트 전체 거래건수`,
    shortName: `${args.scopeLabel} 거래`,
    unit: "count",
    layer: "real_estate",
    source: {
      provider: "국토교통부",
      dataset: args.dataset ?? "아파트 매매 실거래 월별 집계",
      url: "https://rt.molit.go.kr/",
      frequency: "monthly",
      quality: args.status === "error" || args.status === "not_collected" ? "unavailable" : "official",
      observationDate: observations.at(-1)?.date,
      collectedAt: args.generatedAt ?? undefined,
      nativeUnit: "건",
      note: "선택 지역의 가격구간별 거래건수를 동일 월에 합산합니다. 신고·해제 반영으로 최근월은 수정될 수 있습니다."
    },
    observations
  };
}
